import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'

import BusyOverlay from 'components/common/BusyOverlay'
import useAuth from 'hooks/use-auth'

/**
 * This list will provide sane default values for feature flags in the event that LD has an outage
 * and the LD SDK can't retrieve the feature flags from LD's servers
 */
const DEFAULT_FEATURE_FLAG_VALUES = {
  // INTENDED FOR LONGER-TERM USE
  'max-node-nesting': 5, // not a boolean value - 5 is the initial default
  // ACTIVELY USED FEATURE FLAGS BELOW - DO NOT REMOVE
  'custom-rbac': false,
  'aws-auth-iam-role': false,
  'ad-hoc-service-attack': false,
  'gremlin-aws-health-checks': false,
  'thread-bomb': false,
  'swagger-webapp-new-version': false,
  // NEED MORE INFO ON THESE FLAGS
  'cancel-subscription': false,
  'disable-process-collation': false,
  'scenario-observers': false,
  'parallel-attacks': false
}

function useUserContext() {
  const { currentTeamId, currentTeam, companyId, session } = useAuth()

  const userContext = useMemo(
    () => ({
      key: session.identifier,
      email: session.identifier,
      custom: {
        companyId: companyId,
        companyName: session.company_name,
        teamName: currentTeam.name,
        teamId: currentTeamId,
        role: currentTeam.role
      }
    }),
    [
      companyId,
      currentTeam.name,
      currentTeam.role,
      currentTeamId,
      session.company_name,
      session.identifier
    ]
  )
  return userContext
}

const LaunchDarklyClientLoader: React.FC<{ children: React.ReactElement }> = ({
  children
}) => {
  const ldClient = useLDClient()
  const userContext = useUserContext()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    async function identify(user) {
      try {
        await ldClient.identify(user)
      } catch (e) {
        // swallow ld error
      }
    }

    if (isReady) {
      identify(userContext)
    }
  }, [
    isReady,
    userContext,
    ldClient,
    userContext.custom.teamId,
    userContext.email
  ])

  useEffect(() => {
    ldClient &&
      ldClient
        .waitUntilReady()
        .then(() => {
          setIsReady(true)
          // Set on window like the default client would so we can use this in other areas
          window.ldClient = ldClient
        })
        .catch(() => {}) // swallow ld error
  }, [ldClient])

  return isReady ? children : <BusyOverlay />
}

export const LaunchDarklyProvider: React.FC<
  {
    enabled?: boolean
  } & PropsWithChildren<any>
> = ({ children, enabled }) => {
  const userContext = useUserContext()

  if (!enabled) {
    return children
  }

  const isRunningInCypress = Boolean(window.Cypress)

  return (
    <LDProvider
      clientSideID={process.env.REACT_APP_LAUNCHDARKLY_CLIENT_SIDE_ID}
      deferInitialization={false}
      flags={DEFAULT_FEATURE_FLAG_VALUES}
      options={isRunningInCypress && { streaming: false, sendEvents: false }}
      user={{ ...userContext, kind: 'user' }}
    >
      <LaunchDarklyClientLoader>{children}</LaunchDarklyClientLoader>
    </LDProvider>
  )
}
